import { useState, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { supabase, type Creator } from "@/lib/supabaseClient";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

export default function FeaturedCreatorsPage() {
  const [activeTab, setActiveTab] = useState<"featured" | "all">("featured");
  const [creators, setCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Featured Creators — Top Junk Journal & Snail Mail Makers | Junk & Post";
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", "Meet our featured junk journal and snail mail creators. Discover top-rated makers of handmade ephemera, washi tape, stamps, stickers, and pen pal supplies.");
    setMeta("og:title", "Featured Creators — Top Junk Journal & Snail Mail Makers | Junk & Post", true);
    setMeta("og:description", "Meet our featured junk journal and snail mail creators. Discover top-rated makers of handmade ephemera, washi tape, and pen pal supplies.", true);
    setMeta("og:url", `${SITE_URL}/featured`, true);

    const existing = document.getElementById("schema-featured");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = "schema-featured";
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Featured Creators — Junk Journal & Snail Mail Makers",
      "description": "Meet our featured junk journal and snail mail creators. Discover top-rated makers of handmade ephemera, washi tape, stamps, stickers, and pen pal supplies.",
      "url": `${SITE_URL}/featured`,
      "isPartOf": { "@type": "WebSite", "url": SITE_URL, "name": "Junk & Post" }
    });
    document.head.appendChild(script);

    return () => {
      document.getElementById("schema-featured")?.remove();
    };
  }, []);

  useEffect(() => {
    const fetchCreators = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("creators")
        .select("*")
        .eq("approved", true)
        .order("featured", { ascending: false })
        .order("rating", { ascending: false });
      if (!error && data) setCreators(data as Creator[]);
      setLoading(false);
    };
    fetchCreators();
  }, []);

  const displayed =
    activeTab === "featured" ? creators.filter((c) => c.featured) : creators;

  const featuredCount = creators.filter((c) => c.featured).length;

  const tags = (creator: Creator) =>
    (creator.categories || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

  return (
    <div className="min-h-screen bg-[#faf8f4]">
      <Navbar />

      {/* Header */}
      <div className="pt-24 pb-12 bg-[#faf8f4] border-b border-[#ede5d8]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f5ede0] text-[#8b5e3c] text-xs font-semibold uppercase tracking-widest mb-4">
            <i className="ri-sparkling-line"></i>
            Creators
          </div>
          <h1
            className="text-5xl font-bold text-[#2c1e0f] mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Featured Creators
          </h1>
          <p className="text-[#7a6555] max-w-xl mx-auto mb-8">
            Discover the talented makers behind the most beloved junk journal and snail mail
            products in our community.
          </p>

          {/* Tab Switcher */}
          <div className="inline-flex items-center bg-[#f0e8dc] rounded-full px-1 py-1">
            <button
              onClick={() => setActiveTab("featured")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === "featured"
                  ? "bg-white text-[#3a2e1e]"
                  : "text-[#7a6555] hover:text-[#3a2e1e]"
              }`}
            >
              Featured ({featuredCount})
            </button>
            <button
              onClick={() => setActiveTab("all")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === "all"
                  ? "bg-white text-[#3a2e1e]"
                  : "text-[#7a6555] hover:text-[#3a2e1e]"
              }`}
            >
              All Creators ({creators.length})
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-[#ede5d8] overflow-hidden animate-pulse"
              >
                <div className="h-64 bg-[#f0e8dc]"></div>
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-[#f0e8dc] rounded w-3/4"></div>
                  <div className="h-3 bg-[#f0e8dc] rounded w-full"></div>
                  <div className="h-3 bg-[#f0e8dc] rounded w-2/3"></div>
                </div>
              </div>
            ))}
          </div>
        ) : displayed.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-[#c9a87c]">
              <i className="ri-user-search-line text-4xl"></i>
            </div>
            <h3
              className="text-xl font-bold text-[#2c1e0f] mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              No creators yet
            </h3>
            <p className="text-[#7a6555]">Check back soon as we review new submissions!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayed.map((creator) => (
              <div
                key={creator.id}
                className={`bg-white rounded-2xl border overflow-hidden hover:-translate-y-1 transition-transform duration-300 cursor-pointer ${
                  creator.featured ? "border-[#c9a87c]/50" : "border-[#ede5d8]"
                }`}
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden bg-[#f5ede0]">
                  {creator.image_url ? (
                    <img
                      src={creator.image_url}
                      alt={creator.business_name}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#c9a87c]">
                      <i className="ri-image-line text-5xl"></i>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                  {creator.featured && (
                    <div className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#8b5e3c] text-white text-xs font-semibold">
                      <i className="ri-sparkling-line"></i>
                      Featured
                    </div>
                  )}
                  {creator.type && (
                    <div className="absolute top-4 right-4 px-2.5 py-1 rounded-full bg-white/90 text-[#3a2e1e] text-xs font-medium">
                      {creator.type}
                    </div>
                  )}
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3
                      className="font-bold text-white text-xl"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {creator.business_name}
                    </h3>
                    <p className="text-white/80 text-sm">{creator.specialty}</p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3 text-sm text-[#7a6555] flex-wrap">
                    {creator.location && (
                      <span className="flex items-center gap-1">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-map-pin-line text-xs"></i>
                        </div>
                        {creator.location}
                      </span>
                    )}
                    {creator.rating > 0 && (
                      <span className="flex items-center gap-1">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-xs text-[#c9a87c]"></i>
                        </div>
                        {creator.rating}
                      </span>
                    )}
                    {creator.product_count > 0 && (
                      <span className="flex items-center gap-1">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-box-3-line text-xs"></i>
                        </div>
                        {creator.product_count} products
                      </span>
                    )}
                  </div>

                  {creator.bio && (
                    <p className="text-sm text-[#6b5a4a] leading-relaxed mb-4">{creator.bio}</p>
                  )}

                  {tags(creator).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {tags(creator).map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-0.5 rounded-full bg-[#f5ede0] text-[#8b5e3c] text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {creator.website && (
                      <a
                        href={creator.website}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="flex-1 py-2.5 rounded-xl border border-[#c9a87c] text-[#8b5e3c] text-sm font-semibold hover:bg-[#f5ede0] transition-colors cursor-pointer whitespace-nowrap text-center"
                      >
                        Website
                      </a>
                    )}
                    {creator.etsy && (
                      <a
                        href={creator.etsy}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="flex-1 py-2.5 rounded-xl bg-[#f5ede0] text-[#8b5e3c] text-sm font-semibold hover:bg-[#ede0cc] transition-colors cursor-pointer whitespace-nowrap text-center"
                      >
                        Etsy Shop
                      </a>
                    )}
                    {!creator.website && !creator.etsy && (
                      <button className="w-full py-2.5 rounded-xl border border-[#c9a87c] text-[#8b5e3c] text-sm font-semibold hover:bg-[#f5ede0] transition-colors cursor-pointer whitespace-nowrap">
                        View Full Profile
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
